import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowRight, ArrowLeft, Users, Utensils, Hotel, Plane, Car, MapPin, Package, Globe, Calendar, Star, Clipboard, FileText, Clock, Sparkles, Package2, Trash2 } from "lucide-react";
import { PackageData } from "@/pages/CreatePackage";
import { ProgressSteps } from "./ProgressSteps";
import { useCountries } from "@/hooks/useCountries";
import { useVehicleTypes } from "@/hooks/useTasks";
import { useCities } from "@/hooks/useCities";
import { useHotels } from "@/hooks/useHotels";

interface TripDetailsAndProgramStepProps {
  data: PackageData;
  onUpdate: (data: Partial<PackageData>) => void;
  onNext: () => void;
  onPrev: () => void;
  completedSteps?: number[];
  onStepClick?: (step: number) => void;
  isStepRequired?: (step: number) => boolean;
}

export const TripDetailsAndProgramStep = ({
  data,
  onUpdate,
  onNext,
  onPrev,
  completedSteps = [],
  onStepClick = () => {},
  isStepRequired = () => true
}: TripDetailsAndProgramStepProps) => {
  const { data: countries = [], isLoading: isLoadingCountries } = useCountries();
  const { vehicleTypes = [], isLoading: isLoadingVehicleTypes } = useVehicleTypes();
  const { data: allCities = [], isLoading: isLoadingCities } = useCities();
  const { hotels = [], rooms = [], loading: isLoadingHotels } = useHotels();
  const isLoadingRooms = isLoadingHotels;

  const handleNext = () => {
    if (!data.duration?.trim()) {
      alert("يرجى إدخال تاريخ الوصول");
      return;
    }
    if (data.adultsCount <= 0) {
      alert("يجب أن يكون عدد الكبار أكثر من صفر");
      return;
    }
    onNext();
  };

  const mealPlanOptions = [
    { value: 'breakfast', label: '🍳 الإفطار' },
    { value: 'lunch', label: '🍽️ الغداء' },
    { value: 'dinner', label: '🥘 العشاء' },
    { value: 'none', label: '❌ بدون وجبات' }
  ];

  const addCityRow = () => {
    onUpdate({
      citiesPlan: [
        ...(data.citiesPlan || []),
        {
          countryId: '',
          countryName: '',
          cityId: '',
          cityName: '',
          hotel: '',
          roomType: '',
          transferVehicleTypeId: '',
          transferVehicleTypeName: '',
          tourVehicleTypeId: '',
          tourVehicleTypeName: '',
          meals: { breakfast: false, lunch: false, dinner: false },
          roomsCount: 1,
          nightsCount: 1,
        }
      ]
    });
  };

  const updateCityRow = (index: number, patch: any) => {
    const rows = [...(data.citiesPlan || [])];
    rows[index] = { ...rows[index], ...patch };
    onUpdate({ citiesPlan: rows });
  };

  const removeCityRow = (index: number) => {
    const rows = (data.citiesPlan || []).filter((_, i) => i !== index);
    onUpdate({ citiesPlan: rows });
  };

  return (
    <div className="space-y-6 pb-32">
      {/* معلومات العرض السياحي */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-2xl">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl shadow-lg">
              <Clipboard className="w-6 h-6 text-white" />
            </div>
            معلومات العرض السياحي
            <div className="flex-1 h-px bg-gradient-to-r from-blue-200 via-indigo-200 to-transparent"></div>
            <Sparkles className="w-5 h-5 text-blue-400" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* اسم العرض والوجهة وتاريخ الوصول في سطر واحد */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* اسم العرض */}
            <div className="space-y-3">
              <Label htmlFor="packageName" className="flex items-center gap-2 font-medium text-gray-700 text-base">
                <Star className="w-5 h-5 text-amber-500" />
                اسم العرض السياحي
              </Label>
              <Input 
                id="packageName" 
                placeholder="مثال: رحلة استكشافية إلى تركيا - 7 أيام من الإثارة والتشويق" 
                value={data.packageName || ''} 
                onChange={e => onUpdate({ packageName: e.target.value })} 
                className="h-14 text-lg border-2 focus:border-blue-500 bg-white shadow-sm font-medium" 
              />
            </div>

            {/* الوجهة */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 font-medium text-gray-700 text-base">
                <Globe className="w-5 h-5 text-green-500" />
                الوجهة السياحية *
              </Label>
              <Select value={data.destination} onValueChange={value => onUpdate({ destination: value })}>
                <SelectTrigger className="h-14 border-2 focus:border-green-500 bg-white shadow-sm">
                  <SelectValue placeholder="🌍 اختر الوجهة السياحية" />
                </SelectTrigger>
                <SelectContent className="bg-white border-2 border-green-100 z-50">
                  {isLoadingCountries ? (
                    <SelectItem value="loading" disabled className="text-gray-500">
                      جاري التحميل...
                    </SelectItem>
                  ) : (
                    <>
                      {countries.map(country => (
                        <SelectItem key={country.id} value={country.name_ar} className="hover:bg-green-50 text-lg">
                          <span className="flex items-center gap-3">
                            <span className="text-xl">{country.flag_emoji || '🌍'}</span>
                            {country.name_ar}
                          </span>
                        </SelectItem>
                      ))}
                      <SelectItem value="وجهة أخرى" className="hover:bg-green-50 text-lg">
                        <span className="flex items-center gap-3">
                          <span className="text-xl">🌍</span>
                          وجهة أخرى
                        </span>
                      </SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              {data.destination === 'وجهة أخرى' && (
                <Input 
                  placeholder="🗺️ أدخل الوجهة المخصصة" 
                  value={data.destination} 
                  onChange={e => onUpdate({ destination: e.target.value })} 
                  className="h-12 border-2 focus:border-green-500 bg-white shadow-sm" 
                />
              )}
            </div>

            {/* تاريخ الوصول */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 font-medium text-gray-700 text-base">
                <Calendar className="w-5 h-5 text-purple-500" />
                تاريخ الوصول *
              </Label>
              <Input 
                type="date" 
                value={data.duration || ''} 
                onChange={e => onUpdate({ duration: e.target.value })} 
                className="h-14 border-2 focus:border-purple-500 bg-white shadow-sm text-lg" 
                required 
              />
            </div>

            {/* عدد الليالي */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2 font-medium text-gray-700 text-base">
                <Clock className="w-5 h-5 text-orange-500" />
                عدد الليالي *
              </Label>
              <div className="flex items-center justify-center gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  className="h-12 w-12 rounded-full border-2 border-orange-300 hover:border-orange-500" 
                  onClick={() => onUpdate({ numberOfNights: Math.max(1, (data.numberOfNights || 7) - 1) })}
                >
                  -
                </Button>
                <div className="bg-white border-2 border-orange-300 rounded-lg px-6 py-3 min-w-[80px] text-center font-bold text-lg">
                  {data.numberOfNights || 7}
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  className="h-12 w-12 rounded-full border-2 border-orange-300 hover:border-orange-500" 
                  onClick={() => onUpdate({ numberOfNights: (data.numberOfNights || 7) + 1 })}
                >
                  +
                </Button>
              </div>
            </div>
          </div>

          {/* تفاصيل الرحلة والمسافرين */}
          <div className="bg-white rounded-xl p-6 border-2 border-blue-100 shadow-sm space-y-6">
            <h4 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              تفاصيل الرحلة والمسافرين
            </h4>
            
            {/* البالغون والأطفال والرضع والغرف */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* البالغون */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2 font-medium text-gray-700 text-base">
                  <Users className="w-5 h-5 text-green-500" />
                  البالغون * (فوق 12 سنة)
                </Label>
                <div className="flex items-center justify-center gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon" 
                    className="h-12 w-12 rounded-full border-2 border-green-300 hover:border-green-500" 
                    onClick={() => onUpdate({ adultsCount: Math.max(1, (data.adultsCount || 2) - 1) })}
                  >
                    -
                  </Button>
                  <div className="bg-white border-2 border-green-300 rounded-lg px-6 py-3 min-w-[80px] text-center font-bold text-lg">
                    {data.adultsCount || 2}
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon" 
                    className="h-12 w-12 rounded-full border-2 border-green-300 hover:border-green-500" 
                    onClick={() => onUpdate({ adultsCount: (data.adultsCount || 2) + 1 })}
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* الأطفال */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2 font-medium text-gray-700 text-base">
                  <Users className="w-5 h-5 text-blue-500" />
                  الأطفال (تحت 12 سنة)
                </Label>
                <div className="flex items-center justify-center gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon" 
                    className="h-12 w-12 rounded-full border-2 border-blue-300 hover:border-blue-500" 
                    onClick={() => onUpdate({ childrenCount: Math.max(0, (data.childrenCount || 0) - 1) })}
                  >
                    -
                  </Button>
                  <div className="bg-white border-2 border-blue-300 rounded-lg px-6 py-3 min-w-[80px] text-center font-bold text-lg">
                    {data.childrenCount || 0}
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon" 
                    className="h-12 w-12 rounded-full border-2 border-blue-300 hover:border-blue-500" 
                    onClick={() => onUpdate({ childrenCount: (data.childrenCount || 0) + 1 })}
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* الرضع */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2 font-medium text-gray-700 text-base">
                  <Users className="w-5 h-5 text-pink-500" />
                  الرضع (تحت سنتين)
                </Label>
                <div className="flex items-center justify-center gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon" 
                    className="h-12 w-12 rounded-full border-2 border-pink-300 hover:border-pink-500" 
                    onClick={() => onUpdate({ infantsCount: Math.max(0, (data.infantsCount || 0) - 1) })}
                  >
                    -
                  </Button>
                  <div className="bg-white border-2 border-pink-300 rounded-lg px-6 py-3 min-w-[80px] text-center font-bold text-lg">
                    {data.infantsCount || 0}
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon" 
                    className="h-12 w-12 rounded-full border-2 border-pink-300 hover:border-pink-500" 
                    onClick={() => onUpdate({ infantsCount: (data.infantsCount || 0) + 1 })}
                  >
                    +
                  </Button>
                </div>
              </div>

              {/* الغرف */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2 font-medium text-gray-700 text-base">
                  <Hotel className="w-5 h-5 text-purple-500" />
                  الغرف (عدد الغرف المطلوبة)
                </Label>
                <div className="flex items-center justify-center gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon" 
                    className="h-12 w-12 rounded-full border-2 border-purple-300 hover:border-purple-500" 
                    onClick={() => onUpdate({ numberOfRooms: Math.max(1, (data.numberOfRooms || 1) - 1) })}
                  >
                    -
                  </Button>
                  <div className="bg-white border-2 border-purple-300 rounded-lg px-6 py-3 min-w-[80px] text-center font-bold text-lg">
                    {data.numberOfRooms || 1}
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon" 
                    className="h-12 w-12 rounded-full border-2 border-purple-300 hover:border-purple-500" 
                    onClick={() => onUpdate({ numberOfRooms: (data.numberOfRooms || 1) + 1 })}
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* البرنامج والخدمات المشمولة */}
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-100 rounded-xl p-6 space-y-6">
            <h4 className="text-xl font-bold text-amber-800 mb-4 flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-lg shadow-sm">
                <Package className="w-5 h-5 text-white" />
              </div>
              البرنامج والخدمات المشمولة
            </h4>
            
            {/* مربعات الخدمات مع dots التحكم */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* الفنادق */}
              <div className={`flex items-center space-x-2 space-x-reverse p-4 rounded-lg border transition-all duration-200 ${
                data.includesHotels 
                  ? 'bg-blue-50 border-blue-300 shadow-sm' 
                  : 'bg-white border-amber-200 hover:border-amber-300'
              }`}>
                <div 
                  className={`w-4 h-4 rounded-full border-2 cursor-pointer transition-all duration-200 ${
                    data.includesHotels 
                      ? 'bg-blue-500 border-blue-500' 
                      : 'border-gray-300 hover:border-blue-400'
                  }`}
                  onClick={() => onUpdate({ includesHotels: !data.includesHotels })}
                />
                <Label 
                  className="flex items-center gap-2 cursor-pointer font-medium text-sm"
                  onClick={() => onUpdate({ includesHotels: !data.includesHotels })}
                >
                  <Hotel className={`w-4 h-4 ${data.includesHotels ? 'text-blue-600' : 'text-blue-500'}`} />
                  الفنادق والإقامة
                </Label>
              </div>

              {/* الطيران الدولي */}
              <div className={`flex items-center space-x-2 space-x-reverse p-4 rounded-lg border transition-all duration-200 ${
                data.includesInternationalFlights 
                  ? 'bg-sky-50 border-sky-300 shadow-sm' 
                  : 'bg-white border-amber-200 hover:border-amber-300'
              }`}>
                <div 
                  className={`w-4 h-4 rounded-full border-2 cursor-pointer transition-all duration-200 ${
                    data.includesInternationalFlights 
                      ? 'bg-sky-500 border-sky-500' 
                      : 'border-gray-300 hover:border-sky-400'
                  }`}
                  onClick={() => onUpdate({ includesInternationalFlights: !data.includesInternationalFlights })}
                />
                <Label 
                  className="flex items-center gap-2 cursor-pointer font-medium text-sm"
                  onClick={() => onUpdate({ includesInternationalFlights: !data.includesInternationalFlights })}
                >
                  <Plane className={`w-4 h-4 ${data.includesInternationalFlights ? 'text-sky-600' : 'text-sky-500'}`} />
                  الطيران الدولي
                </Label>
              </div>

              {/* الطيران الداخلي */}
              <div className={`flex items-center space-x-2 space-x-reverse p-4 rounded-lg border transition-all duration-200 ${
                data.includesDomesticFlights 
                  ? 'bg-cyan-50 border-cyan-300 shadow-sm' 
                  : 'bg-white border-amber-200 hover:border-amber-300'
              }`}>
                <div 
                  className={`w-4 h-4 rounded-full border-2 cursor-pointer transition-all duration-200 ${
                    data.includesDomesticFlights 
                      ? 'bg-cyan-500 border-cyan-500' 
                      : 'border-gray-300 hover:border-cyan-400'
                  }`}
                  onClick={() => onUpdate({ includesDomesticFlights: !data.includesDomesticFlights })}
                />
                <Label 
                  className="flex items-center gap-2 cursor-pointer font-medium text-sm"
                  onClick={() => onUpdate({ includesDomesticFlights: !data.includesDomesticFlights })}
                >
                  <Plane className={`w-4 h-4 ${data.includesDomesticFlights ? 'text-cyan-600' : 'text-cyan-500'}`} />
                  الطيران الداخلي
                </Label>
              </div>

              {/* المواصلات */}
              <div className={`flex items-center space-x-2 space-x-reverse p-4 rounded-lg border transition-all duration-200 ${
                data.includesTransport 
                  ? 'bg-green-50 border-green-300 shadow-sm' 
                  : 'bg-white border-amber-200 hover:border-amber-300'
              }`}>
                <div 
                  className={`w-4 h-4 rounded-full border-2 cursor-pointer transition-all duration-200 ${
                    data.includesTransport 
                      ? 'bg-green-500 border-green-500' 
                      : 'border-gray-300 hover:border-green-400'
                  }`}
                  onClick={() => onUpdate({ includesTransport: !data.includesTransport })}
                />
                <Label 
                  className="flex items-center gap-2 cursor-pointer font-medium text-sm"
                  onClick={() => onUpdate({ includesTransport: !data.includesTransport })}
                >
                  <Car className={`w-4 h-4 ${data.includesTransport ? 'text-green-600' : 'text-green-500'}`} />
                  المواصلات
                </Label>
              </div>

              {/* الجولات */}
              <div className={`flex items-center space-x-2 space-x-reverse p-4 rounded-lg border transition-all duration-200 ${
                data.includesTours 
                  ? 'bg-purple-50 border-purple-300 shadow-sm' 
                  : 'bg-white border-amber-200 hover:border-amber-300'
              }`}>
                <div 
                  className={`w-4 h-4 rounded-full border-2 cursor-pointer transition-all duration-200 ${
                    data.includesTours 
                      ? 'bg-purple-500 border-purple-500' 
                      : 'border-gray-300 hover:border-purple-400'
                  }`}
                  onClick={() => onUpdate({ includesTours: !data.includesTours })}
                />
                <Label 
                  className="flex items-center gap-2 cursor-pointer font-medium text-sm"
                  onClick={() => onUpdate({ includesTours: !data.includesTours })}
                >
                  <MapPin className={`w-4 h-4 ${data.includesTours ? 'text-purple-600' : 'text-purple-500'}`} />
                  الجولات السياحية
                </Label>
              </div>

              {/* الخدمات */}
              <div className={`flex items-center space-x-2 space-x-reverse p-4 rounded-lg border transition-all duration-200 ${
                data.includesServices 
                  ? 'bg-orange-50 border-orange-300 shadow-sm' 
                  : 'bg-white border-amber-200 hover:border-amber-300'
              }`}>
                <div 
                  className={`w-4 h-4 rounded-full border-2 cursor-pointer transition-all duration-200 ${
                    data.includesServices 
                      ? 'bg-orange-500 border-orange-500' 
                      : 'border-gray-300 hover:border-orange-400'
                  }`}
                  onClick={() => onUpdate({ includesServices: !data.includesServices })}
                />
                <Label 
                  className="flex items-center gap-2 cursor-pointer font-medium text-sm"
                  onClick={() => onUpdate({ includesServices: !data.includesServices })}
                >
                  <Star className={`w-4 h-4 ${data.includesServices ? 'text-orange-600' : 'text-orange-500'}`} />
                  خدمات إضافية
                </Label>
              </div>
            </div>

            {/* معلومات توضيحية */}
            <div className="bg-amber-100 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <Package2 className="w-4 h-4" />
                💡 اضغط على النقاط الملونة لتفعيل/إلغاء تفعيل الخدمات. الخطوات المرتبطة ستظهر في شريط التقدم السفلي حسب اختيارك.
              </p>
            </div>

            {/* نوع الوجبات */}
            <div className="space-y-4 pt-6 border-t border-amber-200">
              <Label className="text-lg font-bold text-amber-800 flex items-center gap-2">
                <Utensils className="w-6 h-6 text-amber-600" />
                الوجبات المشمولة
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {mealPlanOptions.map(option => (
                  <div key={option.value} className="flex items-center space-x-2 space-x-reverse p-3 bg-white rounded-lg border border-amber-200">
                    <Checkbox 
                      id={`meals-${option.value}`} 
                      checked={data.mealPlans?.includes(option.value as any) || false} 
                      onCheckedChange={checked => {
                        const currentPlans = data.mealPlans || [];
                        if (checked) {
                          onUpdate({ mealPlans: [...currentPlans, option.value as any] });
                        } else {
                          onUpdate({ mealPlans: currentPlans.filter(plan => plan !== option.value) });
                        }
                      }} 
                    />
                    <Label htmlFor={`meals-${option.value}`} className="cursor-pointer font-medium text-sm">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* استقبال وتوديع وجولة سياحية - مرتبطة بخدمة المواصلات */}
          {data.includesTransport && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* استقبال وتوديع */}
              <div className="bg-white rounded-xl p-6 border-2 border-blue-100 shadow-sm">
                <h4 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                  <Car className="w-5 h-5 text-blue-600" />
                  استقبال وتوديع
                </h4>
                <div className="space-y-3">
                  <Label className="text-base font-medium">عدد السيارات ونوعها</Label>
                  <div className="flex items-center gap-3 flex-nowrap">
                    <div className="flex items-center gap-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon" 
                        className="h-10 w-10 rounded-full" 
                        onClick={() => onUpdate({ receptionCarsCount: Math.max(0, (data.receptionCarsCount || 1) - 1) })}
                      >
                        -
                      </Button>
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg px-3 py-2 min-w-[56px] text-center font-bold">
                        {data.receptionCarsCount || 1}
                      </div>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon" 
                        className="h-10 w-10 rounded-full" 
                        onClick={() => onUpdate({ receptionCarsCount: (data.receptionCarsCount || 1) + 1 })}
                      >
                        +
                      </Button>
                    </div>

                    <Select
                      value={data.receptionVehicleTypeId || ""}
                      onValueChange={(value) => {
                        const vt = vehicleTypes.find(v => v.id === value);
                        onUpdate({
                          receptionVehicleTypeId: value,
                          receptionVehicleTypeName: vt?.name_ar || ''
                        });
                      }}
                    >
                      <SelectTrigger className="h-12 border-2 focus:border-blue-500 bg-white shadow-sm w-[200px] sm:w-[240px]">
                        <SelectValue placeholder="اختر نوع السيارة" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border z-50">
                        {isLoadingVehicleTypes ? (
                          <SelectItem value="loading" disabled>جاري التحميل...</SelectItem>
                        ) : (
                          <>
                            {vehicleTypes.map((v) => (
                              <SelectItem key={v.id} value={v.id}>{v.name_ar}</SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* جولة سياحية */}
              <div className="bg-white rounded-xl p-6 border-2 border-blue-100 shadow-sm">
                <h4 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  جولة سياحية
                </h4>
                <div className="space-y-3">
                  <Label className="text-base font-medium">عدد السيارات ونوعها</Label>
                  <div className="flex items-center gap-3 flex-nowrap">
                    <div className="flex items-center gap-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon" 
                        className="h-10 w-10 rounded-full" 
                        onClick={() => onUpdate({ tourCarsCount: Math.max(0, (data.tourCarsCount || 1) - 1) })}
                      >
                        -
                      </Button>
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg px-3 py-2 min-w-[56px] text-center font-bold">
                        {data.tourCarsCount || 1}
                      </div>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon" 
                        className="h-10 w-10 rounded-full" 
                        onClick={() => onUpdate({ tourCarsCount: (data.tourCarsCount || 1) + 1 })}
                      >
                        +
                      </Button>
                    </div>

                    <Select
                      value={data.tourVehicleTypeId || ""}
                      onValueChange={(value) => {
                        const vt = vehicleTypes.find(v => v.id === value);
                        onUpdate({
                          tourVehicleTypeId: value,
                          tourVehicleTypeName: vt?.name_ar || ''
                        });
                      }}
                    >
                      <SelectTrigger className="h-12 border-2 focus:border-blue-500 bg-white shadow-sm w-[200px] sm:w-[240px]">
                        <SelectValue placeholder="اختر نوع السيارة" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border z-50">
                        {isLoadingVehicleTypes ? (
                          <SelectItem value="loading" disabled>جاري التحميل...</SelectItem>
                        ) : (
                          <>
                            {vehicleTypes.map((v) => (
                              <SelectItem key={v.id} value={v.id}>{v.name_ar}</SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* تفاصيل الطيران - مرتبطة بخدمة الطيران الدولي */}
          {data.includesInternationalFlights && (
            <div className="space-y-6">
              {/* تفاصيل الطيران الدولي */}
              <div className="bg-white rounded-xl p-6 border-2 border-blue-100 shadow-sm">
                <h4 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                  <Plane className="w-5 h-5 text-sky-500" />
                  الطيران الدولي
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* عدد الحقائب */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">عدد الحقائب</Label>
                    <div className="flex items-center gap-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon" 
                        className="h-10 w-10 rounded-full" 
                        onClick={() => onUpdate({ internationalBaggageCount: Math.max(0, (data.internationalBaggageCount || 1) - 1) })}
                      >
                        -
                      </Button>
                      <div className="bg-sky-50 border-2 border-sky-200 rounded-lg px-4 py-2 min-w-[60px] text-center font-bold">
                        {data.internationalBaggageCount || 1}
                      </div>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon" 
                        className="h-10 w-10 rounded-full" 
                        onClick={() => onUpdate({ internationalBaggageCount: (data.internationalBaggageCount || 1) + 1 })}
                      >
                        +
                      </Button>
                    </div>
                  </div>

                  {/* الوزن */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">الوزن (كيلو)</Label>
                    <Input 
                      type="number" 
                      placeholder="مثال: 23" 
                      value={data.internationalBaggageWeight || ''} 
                      onChange={e => onUpdate({ internationalBaggageWeight: parseInt(e.target.value) || 0 })} 
                      className="h-10 border-2 focus:border-sky-500" 
                    />
                  </div>

                  {/* الوزن داخل الطائرة */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">الوزن داخل الطائرة (كيلو)</Label>
                    <Input 
                      type="number" 
                      placeholder="مثال: 7" 
                      value={data.internationalCabinWeight || ''} 
                      onChange={e => onUpdate({ internationalCabinWeight: parseInt(e.target.value) || 0 })} 
                      className="h-10 border-2 focus:border-sky-500" 
                    />
                  </div>
                </div>
              </div>

              {/* تفاصيل الطيران الداخلي */}
              <div className="bg-white rounded-xl p-6 border-2 border-blue-100 shadow-sm">
                <h4 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                  <Plane className="w-5 h-5 text-cyan-500" />
                  الطيران الداخلي
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* عدد الحقائب */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">عدد الحقائب</Label>
                    <div className="flex items-center gap-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon" 
                        className="h-10 w-10 rounded-full" 
                        onClick={() => onUpdate({ domesticBaggageCount: Math.max(0, (data.domesticBaggageCount || 1) - 1) })}
                      >
                        -
                      </Button>
                      <div className="bg-cyan-50 border-2 border-cyan-200 rounded-lg px-4 py-2 min-w-[60px] text-center font-bold">
                        {data.domesticBaggageCount || 1}
                      </div>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon" 
                        className="h-10 w-10 rounded-full" 
                        onClick={() => onUpdate({ domesticBaggageCount: (data.domesticBaggageCount || 1) + 1 })}
                      >
                        +
                      </Button>
                    </div>
                  </div>

                  {/* الوزن */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">الوزن (كيلو)</Label>
                    <Input 
                      type="number" 
                      placeholder="مثال: 20" 
                      value={data.domesticBaggageWeight || ''} 
                      onChange={e => onUpdate({ domesticBaggageWeight: parseInt(e.target.value) || 0 })} 
                      className="h-10 border-2 focus:border-cyan-500" 
                    />
                  </div>

                  {/* الوزن داخل الطائرة */}
                  <div className="space-y-3">
                    <Label className="text-base font-medium">الوزن داخل الطائرة (كيلو)</Label>
                    <Input 
                      type="number" 
                      placeholder="مثال: 5" 
                      value={data.domesticCabinWeight || ''} 
                      onChange={e => onUpdate({ domesticCabinWeight: parseInt(e.target.value) || 0 })} 
                      className="h-10 border-2 focus:border-cyan-500" 
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* المدن - مرتبطة بخدمة الفنادق */}
          {data.includesHotels && (
            <div className="bg-white rounded-xl p-6 border-2 border-blue-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-bold text-blue-800 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  المدن
                </h4>
                <Button onClick={addCityRow} className="h-10">إضافة مدينة</Button>
              </div>

              <div className="space-y-4">
                {(data.citiesPlan || []).map((row, idx) => {
                  const citiesForCountry = allCities.filter((c: any) => c.country_id === row.countryId)

                  return (
                    <div key={idx} className="border-2 border-blue-100 rounded-lg p-4 bg-white shadow-sm">
                      {/* السطر الأول: الدولة + المدينة + الفندق */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* الدولة */}
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">الدولة</Label>
                          <Select
                            value={row.countryId || ""}
                            onValueChange={(value) => {
                              const country = countries.find((c: any) => c.id === value)
                              updateCityRow(idx, {
                                countryId: value,
                                countryName: country?.name_ar || '',
                                cityId: '',
                                cityName: '',
                                hotelId: '',
                                hotelName: '',
                                hotel: '',
                                roomId: '',
                                roomName: '',
                                roomType: ''
                              })
                            }}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="اختر الدولة" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border z-50">
                              {isLoadingCountries ? (
                                <SelectItem value="loading" disabled>جاري التحميل...</SelectItem>
                              ) : (
                                countries
                                  .sort((a: any, b: any) => a.name_ar.localeCompare(b.name_ar, 'ar'))
                                  .map((c: any) => (
                                    <SelectItem key={c.id} value={c.id}>
                                      {c.flag_emoji ? `${c.flag_emoji} ` : ''}{c.name_ar}
                                    </SelectItem>
                                  ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* المدينة */}
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">المدينة</Label>
                          <Select
                            value={row.cityId || ""}
                            onValueChange={(value) => {
                              const city = allCities.find((ci: any) => ci.id === value)
                              const hotelsForCity = hotels.filter((h: any) => h.city === (city?.name_ar || ''))
                              const defaultHotel = hotelsForCity.find((h: any) => h.is_default) || null
                              updateCityRow(idx, {
                                cityId: value,
                                cityName: city?.name_ar || '',
                                hotelId: defaultHotel?.id || '',
                                hotelName: defaultHotel?.name_ar || '',
                                hotel: defaultHotel?.name_ar || '',
                                roomId: '',
                                roomName: '',
                                roomType: ''
                              })
                            }}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder={row.countryId ? "اختر المدينة" : "اختر الدولة أولاً"} />
                            </SelectTrigger>
                            <SelectContent className="bg-white border z-50">
                              {isLoadingCities ? (
                                <SelectItem value="loading" disabled>جاري التحميل...</SelectItem>
                              ) : !row.countryId ? (
                                <SelectItem value="no-country" disabled>اختر الدولة أولاً</SelectItem>
                              ) : citiesForCountry.length === 0 ? (
                                <SelectItem value="no-cities" disabled>لا توجد مدن لهذه الدولة</SelectItem>
                              ) : (
                                citiesForCountry
                                  .sort((a: any, b: any) => a.name_ar.localeCompare(b.name_ar, 'ar'))
                                  .map((ci: any) => (
                                    <SelectItem key={ci.id} value={ci.id}>{ci.name_ar}</SelectItem>
                                  ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* الفندق */}
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">الفندق</Label>
                          <Select
                            value={row.hotelId || ""}
                            onValueChange={(value) => {
                              const hotel = hotels.find((h: any) => h.id === value)
                              updateCityRow(idx, {
                                hotelId: value,
                                hotelName: hotel?.name_ar || '',
                                hotel: hotel?.name_ar || ''
                              })
                            }}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="اختر الفندق" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border z-50">
                              {isLoadingHotels ? (
                                <SelectItem value="loading" disabled>جاري التحميل...</SelectItem>
                              ) : (
                                (() => {
                                  const filteredHotels = hotels.filter((h: any) => !row.cityId || h.city === row.cityName)
                                  const sortedHotels = filteredHotels.sort((a: any, b: any) => {
                                    if (a.is_default && !b.is_default) return -1
                                    if (!a.is_default && b.is_default) return 1
                                    return a.name_ar.localeCompare(b.name_ar, 'ar')
                                  })
                                  return sortedHotels.map((h: any) => (
                                    <SelectItem key={h.id} value={h.id}>
                                      {h.is_default ? '⭐ ' : ''}{h.name_ar} ({h.stars} نجوم){h.is_default ? ' - افتراضي' : ''}
                                    </SelectItem>
                                  ))
                                })()
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* السطر الثاني: بقية الحقول */}
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-4">
                        {/* نوع الغرفة */}
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">نوع الغرفة</Label>
                          <Select
                            value={row.roomId || ""}
                            onValueChange={(value) => {
                              const room = rooms.find((r: any) => r.id === value)
                              updateCityRow(idx, {
                                roomId: value,
                                roomName: room?.name || '',
                                roomType: room?.name || ''
                              })
                            }}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="نوع الغرفة" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border z-50">
                              {isLoadingRooms ? (
                                <SelectItem value="loading" disabled>جاري التحميل...</SelectItem>
                              ) : (
                                rooms
                                  .filter((r: any) => !row.hotelId || r.hotel_id === row.hotelId)
                                  .map((r: any) => (
                                    <SelectItem key={r.id} value={r.id}>
                                      {r.name} - {r.selling_price ? `${r.selling_price} ${r.currency || 'SAR'}` : 'السعر غير محدد'}
                                    </SelectItem>
                                  ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* نوع سيارة النقلات */}
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">نوع سيارة النقلات</Label>
                          <Select
                            value={row.transferVehicleTypeId || ""}
                            onValueChange={(value) => {
                              const vt = vehicleTypes.find((v: any) => v.id === value)
                              updateCityRow(idx, { transferVehicleTypeId: value, transferVehicleTypeName: vt?.name_ar || '' })
                            }}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="نوع النقلات" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border z-50">
                              {isLoadingVehicleTypes ? (
                                <SelectItem value="loading" disabled>جاري التحميل...</SelectItem>
                              ) : (
                                vehicleTypes.map((v: any) => (
                                  <SelectItem key={v.id} value={v.id}>{v.name_ar}</SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* نوع سيارة الجولات */}
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">نوع سيارة الجولات</Label>
                          <Select
                            value={row.tourVehicleTypeId || ""}
                            onValueChange={(value) => {
                              const vt = vehicleTypes.find((v: any) => v.id === value)
                              updateCityRow(idx, { tourVehicleTypeId: value, tourVehicleTypeName: vt?.name_ar || '' })
                            }}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="نوع الجولات" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border z-50">
                              {isLoadingVehicleTypes ? (
                                <SelectItem value="loading" disabled>جاري التحميل...</SelectItem>
                              ) : (
                                vehicleTypes.map((v: any) => (
                                  <SelectItem key={v.id} value={v.id}>{v.name_ar}</SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* الوجبات */}
                        <div className="space-y-1 lg:col-span-2">
                          <Label className="text-sm font-medium">الوجبات</Label>
                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={row.meals?.breakfast || false}
                                onCheckedChange={(checked) => updateCityRow(idx, { meals: { ...row.meals, breakfast: !!checked } })}
                              />
                              <span className="text-sm">الإفطار</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={row.meals?.lunch || false}
                                onCheckedChange={(checked) => updateCityRow(idx, { meals: { ...row.meals, lunch: !!checked } })}
                              />
                              <span className="text-sm">الغداء</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={row.meals?.dinner || false}
                                onCheckedChange={(checked) => updateCityRow(idx, { meals: { ...row.meals, dinner: !!checked } })}
                              />
                              <span className="text-sm">العشاء</span>
                            </div>
                          </div>
                        </div>

                        {/* عدد الغرف */}
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">عدد الغرف</Label>
                          <div className="flex items-center gap-2">
                            <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => updateCityRow(idx, { roomsCount: Math.max(1, (row.roomsCount || 1) - 1) })}>-</Button>
                            <div className="bg-blue-50 border border-blue-200 rounded px-2 py-1 min-w-[40px] text-center text-sm font-medium">{row.roomsCount ?? 1}</div>
                            <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => updateCityRow(idx, { roomsCount: (row.roomsCount || 1) + 1 })}>+</Button>
                          </div>
                        </div>

                        {/* عدد الليالي */}
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">عدد الليالي</Label>
                          <div className="flex items-center gap-2">
                            <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => updateCityRow(idx, { nightsCount: Math.max(1, (row.nightsCount || 1) - 1) })}>-</Button>
                            <div className="bg-purple-50 border border-purple-200 rounded px-2 py-1 min-w-[40px] text-center text-sm font-medium">{row.nightsCount ?? 1}</div>
                            <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => updateCityRow(idx, { nightsCount: (row.nightsCount || 1) + 1 })}>+</Button>
                          </div>
                        </div>

                        {/* حذف */}
                        <div className="flex items-end">
                          <Button variant="outline" size="icon" onClick={() => removeCityRow(idx)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}


          {/* ملاحظات إضافية */}
          <div className="space-y-3">
            <Label htmlFor="notes" className="flex items-center gap-2 font-medium text-gray-700 text-base">
              <FileText className="w-5 h-5 text-teal-500" />
              ملاحظات خاصة للرحلة (اختياري)
            </Label>
            <Textarea 
              id="notes"
              placeholder="أضف أي ملاحظات أو متطلبات خاصة للرحلة مثل: المناسبات الخاصة، احتياجات خاصة للمسافرين، طلبات إضافية..." 
              value={data.notes || ''} 
              onChange={e => onUpdate({ notes: e.target.value })} 
              className="min-h-[100px] border-2 focus:border-teal-500 bg-white shadow-sm resize-none text-base" 
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* أزرار التنقل */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-slate-50 to-gray-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <Button 
              onClick={onPrev} 
              variant="outline" 
              className="flex items-center gap-2 h-12 px-6 border-2 hover:border-gray-400"
            >
              <ArrowLeft className="w-4 h-4" />
              السابق: معلومات العميل
            </Button>
            
            <Button 
              onClick={handleNext} 
              className="flex items-center gap-2 h-12 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              التالي: الفنادق والإقامة
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* شريط التقدم العائم */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg">
        <ProgressSteps 
          currentStep={2}
          completedSteps={completedSteps}
          onStepClick={onStepClick}
          isStepRequired={isStepRequired}
        />
      </div>
    </div>
  );
};